/**
 * Runtime configuration loader.
 *
 * Fetches /config from the SAME HOST that served the bundle — the
 * static server we run in production (see frontend/server.js) proxies
 * that path to the backend so this works out-of-the-box across
 * Railway's two-service layout.
 *
 * Usage:
 *   import { loadConfig, getApiUrl } from './services/config';
 *
 *   await loadConfig();         // call once at app boot
 *   fetch(`${getApiUrl()}/...`) // then use freely
 *
 * getApiUrl() throws if the config hasn't loaded yet — that's
 * intentional: it means a component tried to call the API before the
 * boot gate released, which is a bug worth surfacing loudly.
 */

let _config = null;
let _inflight = null;

const DEFAULT_API_URL = 'http://localhost:8001';

export async function loadConfig() {
  if (_config) return _config;
  if (_inflight) return _inflight;

  _inflight = (async () => {
    try {
      // Relative URL → same host that served index.html. In dev this
      // falls through CRA's proxy; in prod it hits our Node server's
      // /config proxy route.
      const resp = await fetch('/config', {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) throw new Error(`GET /config → ${resp.status}`);
      const data = await resp.json();
      if (!data.api_url) throw new Error('/config missing api_url');
      _config = data;
      return data;
    } catch (err) {
      // Last-resort fallback keeps a broken /config from breaking
      // local dev entirely. We log loudly so it's obvious in the
      // browser console.
      // eslint-disable-next-line no-console
      console.warn(
        `Config load failed (${err.message}); falling back to ${DEFAULT_API_URL}`
      );
      _config = { api_url: DEFAULT_API_URL, version: 'unknown' };
      return _config;
    } finally {
      _inflight = null;
    }
  })();

  return _inflight;
}

export function getApiUrl() {
  if (!_config) {
    throw new Error(
      'getApiUrl() called before loadConfig() resolved — ' +
        'make sure the app awaits loadConfig() at boot.'
    );
  }
  // Trim trailing slash so callers can always interpolate `${base}/path`.
  return _config.api_url.replace(/\/+$/, '');
}

export function getVersion() {
  return _config?.version || 'unknown';
}

// Test-only reset hook. Safe to ignore in production code.
export function __resetConfigForTests() {
  _config = null;
  _inflight = null;
}
